import { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MetricCard, StatusBadge } from "@/components/ui/design";
import { RefreshCw, Activity } from "lucide-react";

const ST = { READY: "success", PARTIAL: "warning", PLACEHOLDER: "info", BROKEN: "danger" };
const cell = (v) => v === "OK" || v === "Terhubung" || v === "Tersedia"
  ? <span className="text-green-700">{v}</span>
  : v === "N/A" ? <span className="text-slate-400">N/A</span>
  : v === "MISSING" || v === "TIDAK ADA" ? <span className="text-red-600 font-medium">{v}</span>
  : <span>{v}</span>;

export default function IntegrationHealth() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("all");
  const [phase, setPhase] = useState("all");
  const load = () => api.get("/admin/integration-health").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Layout><PageHeader title="Integration Health" breadcrumb="Admin" /><div className="p-6 text-sm text-muted-foreground">Memuat status modul...</div></Layout>;

  const s = d.summary;
  const rows = d.modules.filter((m) => (status === "all" || m.status === status) && (phase === "all" || m.phase === phase));

  return (
    <Layout>
      <PageHeader breadcrumb="Admin" title="Integration Health Center" subtitle="Status modul langsung (bukan nilai statis) — rute, backend, persistensi, bukti, gerbang & ekspor"
        actions={<Button size="sm" onClick={load} data-testid="ih-refresh"><RefreshCw size={14} className="mr-1" />Cek Ulang</Button>} />
      <div className="p-6 space-y-5" data-testid="integration-health-page">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCard label="Total Modul" value={s.total} icon={Activity} testid="ih-total" />
          <MetricCard label="READY" value={s.ready} tone="success" testid="ih-ready" />
          <MetricCard label="PARTIAL" value={s.partial} tone={s.partial ? "warning" : "neutral"} testid="ih-partial" />
          <MetricCard label="PLACEHOLDER" value={s.placeholder} tone={s.placeholder ? "info" : "neutral"} />
          <MetricCard label="BROKEN" value={s.broken} tone={s.broken ? "danger" : "neutral"} testid="ih-broken" />
        </div>
        <div className="text-[11px] text-muted-foreground">Pemeriksaan terakhir: {(s.last_check || "").slice(0, 19).replace("T", " ")}</div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs w-44" data-testid="ih-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {["READY", "PARTIAL", "PLACEHOLDER", "BROKEN"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={phase} onValueChange={setPhase}>
            <SelectTrigger className="h-8 text-xs w-44" data-testid="ih-filter-phase"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Fase</SelectItem>
              {d.phases.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{rows.length} modul</span>
        </div>

        <div className="section-card overflow-x-auto">
          <table className="w-full audit-table">
            <thead><tr>
              <th>Modul</th><th>Fase</th><th>Rute</th><th>Backend</th><th>Persistensi DB</th><th>Bukti</th>
              <th>Completeness</th><th>Quality Gate</th><th>Ekspor</th><th>Isu Diketahui</th><th>Status</th>
            </tr></thead>
            <tbody>{rows.map((m) => (
              <tr key={m.module} data-testid={`ih-row-${m.module}`}>
                <td className="text-xs font-medium">{m.module}</td>
                <td className="text-xs">{m.phase}</td>
                <td className="text-[10px] font-mono text-muted-foreground">{m.route}</td>
                <td className="text-xs">{cell(m.backend_api)}</td>
                <td className="text-xs">{cell(m.db_persistence)}</td>
                <td className="text-xs">{cell(m.evidence_upload)}</td>
                <td className="text-xs">{cell(m.completeness_engine)}</td>
                <td className="text-xs">{cell(m.quality_gate)}</td>
                <td className="text-xs">{cell(m.export)}</td>
                <td className="text-[11px] text-amber-700 max-w-[180px]">{m.known_issue || "—"}</td>
                <td><StatusBadge tone={ST[m.status]}>{m.status}</StatusBadge></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
